import {ConnectButton} from '@rainbow-me/rainbowkit';
import {AppSidebar} from "@/components/app-sidebar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/breadcrumb";
import {Separator} from "@/components/separator";
import {SidebarInset, SidebarProvider, SidebarTrigger} from "@/components/sidebar";
import {Link} from "react-router-dom";
import {RiLoader4Line} from "@remixicon/react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/card";
import { useMemo, type ReactNode } from 'react';
import { formatEther } from 'viem';
import { useBalance, useReadContract } from 'wagmi';
import { LRSNODE_V3_CONTRACT_CONFIG } from '@/configs/lrsnode.config';

export default function LRSNodeStatsPage() {
  const node3Ready = !!LRSNODE_V3_CONTRACT_CONFIG.address && LRSNODE_V3_CONTRACT_CONFIG.address.length > 2;

  // Minimal ABI fragments for additional global stats (not in current lrsnode.config.ts ABI)
  const LRSNODE3_STATS_ABI = useMemo(() => {
    return [
      { name: 'lastNodeKeyID', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
      { name: 'currentKeyBuy', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
      { name: 'priceOracle', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
      { name: 'totalRewardsDistributed', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
      { name: 'totalRewardsClaimed', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
      { name: 'totalRewardsUnclaimed', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
    ] as const;
  }, []);

  const ORACLE_ABI = useMemo(() => {
    return [
      {
        name: 'lrsUsdPriceE8',
        type: 'function',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ type: 'uint256', name: 'priceE8' }, { type: 'uint256', name: 'updatedAt' }],
      },
    ] as const;
  }, []);

  const { data: node3Balance } = useBalance({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    query: { enabled: node3Ready },
  });

  const { data: lastNodeKeyID } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE3_STATS_ABI,
    functionName: 'lastNodeKeyID',
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const { data: currentKeyBuy } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE3_STATS_ABI,
    functionName: 'currentKeyBuy',
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const { data: priceOracle } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE3_STATS_ABI,
    functionName: 'priceOracle',
    query: { enabled: node3Ready },
  }) as { data: `0x${string}` | undefined };

  const { data: totalRewardsDistributed } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE3_STATS_ABI,
    functionName: 'totalRewardsDistributed',
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const { data: totalRewardsClaimed } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE3_STATS_ABI,
    functionName: 'totalRewardsClaimed',
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const { data: totalRewardsUnclaimed } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE3_STATS_ABI,
    functionName: 'totalRewardsUnclaimed',
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const { data: oraclePriceTuple } = useReadContract({
    address: priceOracle,
    abi: ORACLE_ABI,
    functionName: 'lrsUsdPriceE8',
    query: { enabled: node3Ready && !!priceOracle && priceOracle !== '0x0000000000000000000000000000000000000000' },
  }) as { data: readonly [bigint, bigint] | undefined };

  const oraclePriceE8 = oraclePriceTuple?.[0];
  const oracleUpdatedAt = oraclePriceTuple?.[1];

  const PLAN_IDS = useMemo(() => [1n, 2n] as const, []);

  const { data: plan1 } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'plans',
    args: [PLAN_IDS[0]],
    query: { enabled: node3Ready },
  }) as { data: readonly [bigint, bigint, bigint, bigint, boolean] | undefined };

  const { data: plan2 } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'plans',
    args: [PLAN_IDS[1]],
    query: { enabled: node3Ready },
  }) as { data: readonly [bigint, bigint, bigint, bigint, boolean] | undefined };

  const plan1Parsed = useMemo(() => {
    if (!plan1) return null;
    const [usdCents, duration, maxStock, soldStock, isActive] = plan1;
    return { usdCents, duration, maxStock, soldStock, isActive };
  }, [plan1]);

  const plan2Parsed = useMemo(() => {
    if (!plan2) return null;
    const [usdCents, duration, maxStock, soldStock, isActive] = plan2;
    return { usdCents, duration, maxStock, soldStock, isActive };
  }, [plan2]);

  const { data: planAvailable1 } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'planAvailable',
    args: [PLAN_IDS[0]],
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const { data: planAvailable2 } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'planAvailable',
    args: [PLAN_IDS[1]],
    query: { enabled: node3Ready },
  }) as { data: bigint | undefined };

  const formatAddress = (addr?: string) => {
    if (!addr) return '—';
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
  };

  const formatLrs = (wei?: bigint) => {
    if (wei === undefined) return '—';
    return `${parseFloat(formatEther(wei)).toFixed(6)} LRS`;
  };

  const StatCard = ({
    label,
    value,
    sublabel,
    isLoading,
  }: {
    label: string;
    value: ReactNode;
    sublabel?: ReactNode;
    isLoading?: boolean;
  }) => {
    return (
      <Card className="bg-sidebar border border-border hover:border-primary/40 transition-colors duration-200">
        <CardContent className="p-5">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-1 h-10 bg-primary/90 rounded-full" aria-hidden />
            <div className="min-w-0">
              {isLoading ? (
                <RiLoader4Line className="size-7 animate-spin text-primary" aria-hidden />
              ) : (
                <div className="text-3xl font-semibold tabular-nums leading-none">{value}</div>
              )}
              {sublabel ? (
                <div className="mt-1 text-xs text-muted-foreground">{sublabel}</div>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/lrsnode">LRSNode</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Stats</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-primary rounded-full" aria-hidden />
                    <h1 className="text-3xl font-bold tracking-tight">LRSNode Stats</h1>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    A data-dense snapshot of Node3 key issuance, plan inventory, rewards distribution, and oracle freshness.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-8">
                <StatCard
                  label="Total LRSNode"
                  isLoading={node3Ready && lastNodeKeyID === undefined}
                  value={node3Ready ? (lastNodeKeyID !== undefined ? lastNodeKeyID.toString() : '—') : 'Not configured'}
                />

                <StatCard
                  label="Node3 Total Buys"
                  isLoading={node3Ready && currentKeyBuy === undefined}
                  value={node3Ready ? (currentKeyBuy !== undefined ? currentKeyBuy.toString() : '—') : 'Not configured'}
                />

                <StatCard
                  label="Pro Key Inventory"
                  isLoading={node3Ready && !plan1Parsed}
                  value={
                    node3Ready
                      ? plan1Parsed
                        ? `${plan1Parsed.maxStock.toString()} / ${plan1Parsed.soldStock.toString()}`
                        : '—'
                      : 'Not configured'
                  }
                  sublabel={
                    node3Ready ? (
                      <>Remaining: {planAvailable1 !== undefined ? planAvailable1.toString() : '—'}</>
                    ) : undefined
                  }
                />

                <StatCard
                  label="Lite Key Inventory"
                  isLoading={node3Ready && !plan2Parsed}
                  value={
                    node3Ready
                      ? plan2Parsed
                        ? `${plan2Parsed.maxStock.toString()} / ${plan2Parsed.soldStock.toString()}`
                        : '—'
                      : 'Not configured'
                  }
                  sublabel={
                    node3Ready ? (
                      <>Remaining: {planAvailable2 !== undefined ? planAvailable2.toString() : '—'}</>
                    ) : undefined
                  }
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-10">
                <Card className="bg-sidebar border border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Global rewards</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {!node3Ready ? (
                      <div className="text-sm text-muted-foreground">Set `VITE_LRSNODE_3_CONTRACT_ADDRESS` to load Node3 stats.</div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Distributed</span>
                          <span className="font-medium tabular-nums">{formatLrs(totalRewardsDistributed)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Claimed</span>
                          <span className="font-medium tabular-nums">{formatLrs(totalRewardsClaimed)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                          <span className="text-muted-foreground">Unclaimed (tracked)</span>
                          <span className="font-semibold tabular-nums">{formatLrs(totalRewardsUnclaimed)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Contract balance</span>
                          <span className="font-medium tabular-nums">
                            {node3Balance?.value !== undefined ? `${parseFloat(formatEther(node3Balance.value)).toFixed(6)} LRS` : '—'}
                          </span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-sidebar border border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Oracle</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {!node3Ready ? (
                      <div className="text-sm text-muted-foreground">Not configured.</div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">LRS/USD</span>
                          <span className="font-medium tabular-nums">{oraclePriceE8 !== undefined ? (parseInt(oraclePriceE8.toString()) / 100000000).toFixed(8) : '—'}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Updated</span>
                          <span className="font-medium tabular-nums">
                            {oracleUpdatedAt !== undefined ? new Date(Number(oracleUpdatedAt) * 1000).toLocaleString() : '—'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                          <span className="text-muted-foreground">Oracle address</span>
                          <span className="font-mono text-xs">{formatAddress(priceOracle)}</span>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}



